import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import "../dashboard.css";

/* ───────────────────────────── Date helpers ───────────────────────────── */

const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (String(dt) === "Invalid Date") return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(1)}%`;
}

/* ────────────────── Validation + formatting helpers ────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE7_RE = /^\d{3}-\d{4}$/;
const USERNAME_RE = /^[A-Za-z0-9](?:[A-Za-z0-9._-]{1,28}[A-Za-z0-9])?$/;

function normalizePhoneToDash(v) {
  const digits = String(v || "").replace(/\D/g, "").slice(0, 7);
  if (digits.length <= 3) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
}

/* ─────────────────────── Icelandic labels for “sex” ─────────────────────── */

const SEX_LABELS_IS = {
  male: "Karl",
  female: "Kona",
  nonbinary: "Hán / kynsegin",
  other: "Annað",
};

const SEX_OPTIONS = [
  { value: "male", label: SEX_LABELS_IS.male },
  { value: "female", label: SEX_LABELS_IS.female },
  { value: "nonbinary", label: SEX_LABELS_IS.nonbinary },
  { value: "other", label: SEX_LABELS_IS.other },
];

/* ─────────────── Tiny inline calendar (DD/MM view, ISO value) ─────────────── */

function MiniDatePickerDMY({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || new Date();
  const [view, setView] = useState(new Date(selected.getFullYear(), selected.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        style={{ width: 160, cursor: "pointer" }}
        placeholder="dd/mm/áááá"
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: 38,
            left: 0,
            background: "#fff",
            border: "1px solid #d7dbe3",
            borderRadius: 8,
            padding: 8,
            zIndex: 50,
            boxShadow: "0 12px 28px rgba(0,0,0,.12)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
            <button className="btn ghost sm" type="button" onClick={prevMonth}>◀</button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>▶</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center", opacity: 0.7, fontSize: 12, marginBottom: 4 }}>
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center" }}>
            {cells.map((c, i) => {
              const isOut = c === null || c.getMonth() !== view.getMonth();
              const active = c && isSameDay(c, selected);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!c}
                  onClick={() => {
                    if (!c) return;
                    onChange?.(dateToIso(c));
                    setOpen(false);
                  }}
                  style={{
                    width: 32,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid #d7dbe3",
                    opacity: isOut ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: c ? "pointer" : "default",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── Editor Modal ───────────────────────────── */

function EditorModal({
  open,
  title,
  multiline = false,
  value,
  setValue,
  placeholder = "",
  error = "",
  onCancel,
  onSave,
}) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
      onMouseDown={onCancel}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 92vw)",
          background: "#fff",
          border: "1px solid #d7dbe3",
          borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,.18)",
          padding: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>{title}</div>

        {multiline ? (
          <textarea
            rows={8}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            style={{ width: "100%", resize: "vertical" }}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            style={{ width: "100%" }}
          />
        )}

        {error ? <div style={{ color: "#b91c1c", marginTop: 8 }}>{error}</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={onCancel}>Hætta við</button>
          <button className="btn sm" onClick={onSave}>Vista</button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── Choice (dropdown) Modal ───────────────────────────── */

function ChoiceModal({ open, title, value, setValue, options = [], onCancel, onSave }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
      onMouseDown={onCancel}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 92vw)",
          background: "#fff",
          border: "1px solid #d7dbe3",
          borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,.18)",
          padding: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>{title}</div>

        <select value={value} onChange={(e) => setValue(e.target.value)} style={{ width: "100%" }}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={onCancel}>Hætta við</button>
          <button className="btn sm" onClick={onSave}>Vista</button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── Admin edit modals ───────────────────────────── */

function AttendanceEditModal({ open, value, setValue, onCancel, onSave }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
      onMouseDown={onCancel}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 92vw)",
          background: "#fff",
          border: "1px solid #d7dbe3",
          borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,.18)",
          padding: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Breyta mætingu</div>

        <label>Dagsetning:</label>
        <input
          type="date"
          value={value.attendance_date || ""}
          onChange={(e) => setValue((v) => ({ ...v, attendance_date: e.target.value }))}
          style={{ width: "100%", marginTop: 6 }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={onCancel}>Hætta við</button>
          <button className="btn sm" onClick={onSave}>Vista</button>
        </div>
      </div>
    </div>
  );
}

function InterviewEditModal({ open, value, setValue, onCancel, onSave }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
      onMouseDown={onCancel}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 92vw)",
          background: "#fff",
          border: "1px solid #d7dbe3",
          borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,.18)",
          padding: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Breyta viðtali</div>

        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <label>Dagsetning:</label>
            <input
              type="date"
              value={value.interview_date || ""}
              onChange={(e) => setValue((v) => ({ ...v, interview_date: e.target.value }))}
              style={{ width: "100%", marginTop: 6 }}
            />
          </div>

          <div>
            <label>Tegund viðtals:</label>
            <select
              value={value.kind || ""}
              onChange={(e) => setValue((v) => ({ ...v, kind: e.target.value }))}
              style={{ width: "100%", marginTop: 6 }}
            >
              <option value="">— Veldu —</option>
              <option value="in_house">Kom í hús</option>
              <option value="remote">Símaviðtal / Fjarfundur</option>
              <option value="no_show">Mætti ekki</option>
            </select>
          </div>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.flex}
              onChange={(e) => setValue((v) => ({ ...v, flex: e.target.checked }))}
            />
            Flex
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.peer_support}
              onChange={(e) => setValue((v) => ({ ...v, peer_support: e.target.checked }))}
            />
            Jafningjastuðningur
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.rehab_planning}
              onChange={(e) => setValue((v) => ({ ...v, rehab_planning: e.target.checked }))}
            />
            Endurhæfingaráætlun
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.rehab_interview}
              onChange={(e) => setValue((v) => ({ ...v, rehab_interview: e.target.checked }))}
            />
            Endurhæfingarviðtal
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.urgent_interview}
              onChange={(e) => setValue((v) => ({ ...v, urgent_interview: e.target.checked }))}
            />
            Bráðaviðtal
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.family_interview}
              onChange={(e) => setValue((v) => ({ ...v, family_interview: e.target.checked }))}
            />
            Fjölskylduviðtal
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.visit}
              onChange={(e) => setValue((v) => ({ ...v, visit: e.target.checked }))}
            />
            Vitjun
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.handleidsla}
              onChange={(e) => setValue((v) => ({ ...v, handleidsla: e.target.checked }))}
            />
            Handleiðsla
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.handleidsla_nemar}
              onChange={(e) => setValue((v) => ({ ...v, handleidsla_nemar: e.target.checked }))}
            />
            Handleiðsla / nemar
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.ororka}
              onChange={(e) => setValue((v) => ({ ...v, ororka: e.target.checked }))}
            />
            Örorka
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={onCancel}>Hætta við</button>
          <button className="btn sm" onClick={onSave}>Vista</button>
        </div>
      </div>
    </div>
  );
}

function IncidentEditModal({ open, value, setValue, onCancel, onSave }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
      onMouseDown={onCancel}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 92vw)",
          background: "#fff",
          border: "1px solid #d7dbe3",
          borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,.18)",
          padding: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Breyta atviki</div>

        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <label>Dagsetning:</label>
            <input
              type="date"
              value={value.incident_date || ""}
              onChange={(e) => setValue((v) => ({ ...v, incident_date: e.target.value }))}
              style={{ width: "100%", marginTop: 6 }}
            />
          </div>

          <div>
            <label>Lýsing:</label>
            <textarea
              rows={6}
              value={value.description || ""}
              onChange={(e) => setValue((v) => ({ ...v, description: e.target.value }))}
              style={{ width: "100%", marginTop: 6 }}
            />
          </div>

          <div>
            <label>Tími:</label>
            <input
              value={value.time_taken || ""}
              onChange={(e) => setValue((v) => ({ ...v, time_taken: e.target.value }))}
              style={{ width: "100%", marginTop: 6 }}
            />
          </div>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.in_house}
              onChange={(e) => setValue((v) => ({ ...v, in_house: e.target.checked }))}
            />
            Í hús
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.on_phone}
              onChange={(e) => setValue((v) => ({ ...v, on_phone: e.target.checked }))}
            />
            Sími
          </label>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!value.email}
              onChange={(e) => setValue((v) => ({ ...v, email: e.target.checked }))}
            />
            Email
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={onCancel}>Hætta við</button>
          <button className="btn sm" onClick={onSave}>Vista</button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── UI bits ───────────────────────────── */

function TabBtn({ active, onClick, children }) {
  return (
    <button
      className={`btn ${active ? "" : "ghost"} sm`}
      style={{ borderRadius: 999, padding: "6px 12px", marginRight: 8 }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function AttendanceStatCard({ label, value, accent = "#111" }) {
  return (
    <div
      style={{
        border: "1px solid #d7dbe3",
        borderRadius: 10,
        background: "#fff",
        padding: "10px 12px",
        minWidth: 150,
      }}
    >
      <div style={{ opacity: 0.75, fontSize: 13 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 22, color: accent, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [role, setRole] = useState("staff");
  const [myId, setMyId] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("overview");
  const [rows, setRows] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabRefresh, setTabRefresh] = useState(0);

  // interviews (as interviewee)
  const [iQ, setIQ] = useState("");
  const [iStart, setIStart] = useState("");
  const [iEnd, setIEnd] = useState("");

  // interviews_taken
  const [takenRows, setTakenRows] = useState([]);
  const [tQ, setTQ] = useState("");
  const [tStart, setTStart] = useState("");
  const [tEnd, setTEnd] = useState("");
  const [tFiltersOpen, setTFiltersOpen] = useState(false);

  const isAdmin = role === "admin";
  const isStaff = role === "staff";
  const isOwnProfile = String(myId) === String(id);

  const canSeeInterviews = isAdmin;
  const canSeeInterviewsTaken = isAdmin || (isStaff && isOwnProfile);

  const canChangePassword = isAdmin || String(myId) === String(summary?.id);

  const canManageRehabAndUraedi = isAdmin || isStaff;

  /* ─────────────── Inline editor modal state ─────────────── */

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTitle, setEditorTitle] = useState("");
  const [editorValue, setEditorValue] = useState("");
  const [editorMultiline, setEditorMultiline] = useState(false);
  const [editorPlaceholder, setEditorPlaceholder] = useState("");
  const [editorError, setEditorError] = useState("");
  const editorValidateRef = useRef(null);
  const editorOnSaveRef = useRef(null);

  /* ─────────────── Attendance / interview / incident edit state ─────────────── */

  const [attendanceEditOpen, setAttendanceEditOpen] = useState(false);
  const [attendanceEdit, setAttendanceEdit] = useState({
    id: null,
    kind: "group",
    attendance_date: "",
  });

  const [interviewEditOpen, setInterviewEditOpen] = useState(false);
  const [interviewEdit, setInterviewEdit] = useState({
    id: null,
    interview_date: "",
    kind: "",
    flex: false,
    peer_support: false,
    rehab_planning: false,
    rehab_interview: false,
    urgent_interview: false,
    family_interview: false,
    visit: false,
    handleidsla: false,
    handleidsla_nemar: false,
    ororka: false,
  });

  const [incidentEditOpen, setIncidentEditOpen] = useState(false);
  const [incidentEdit, setIncidentEdit] = useState({
    id: null,
    incident_date: "",
    description: "",
    time_taken: "",
    in_house: false,
    on_phone: false,
    email: false,
  });

  function openEditor({
    title,
    initial = "",
    multiline = false,
    placeholder = "",
    validate = null,
    onSave = null,
  }) {
    setEditorTitle(title);
    setEditorValue(initial);
    setEditorMultiline(multiline);
    setEditorPlaceholder(placeholder);
    setEditorError("");
    editorValidateRef.current = validate || null;
    editorOnSaveRef.current = onSave || null;
    setEditorOpen(true);
  }

  async function runEditorSave() {
    const validate = editorValidateRef.current;
    if (validate) {
      const msg = validate(editorValue);
      if (msg) {
        setEditorError(msg);
        return;
      }
    }
    try {
      await editorOnSaveRef.current?.(editorValue);
      setEditorOpen(false);
    } catch (e) {
      setEditorError(e?.response?.data?.detail || e?.message || "Tókst ekki að vista.");
    }
  }

  /* ─────────────── Choice modal state ─────────────── */

  const [choiceOpen, setChoiceOpen] = useState(false);
  const [choiceTitle, setChoiceTitle] = useState("");
  const [choiceOptions, setChoiceOptions] = useState([]);
  const [choiceValue, setChoiceValue] = useState("");
  const choiceOnSaveRef = useRef(null);

  function openChoice({ title, options, initial, onSave }) {
    setChoiceTitle(title);
    setChoiceOptions(options || []);
    setChoiceValue(initial || (options?.[0]?.value ?? ""));
    choiceOnSaveRef.current = onSave;
    setChoiceOpen(true);
  }

  async function runChoiceSave() {
    await choiceOnSaveRef.current?.(choiceValue);
    setChoiceOpen(false);
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/api/user/role/", { headers: authHeaders() });
        setRole(r.data.role);
        if (r.data.user_id != null) setMyId(r.data.user_id);
        setIsSuperuser(!!r.data.is_superuser);
      } catch {}
    })();
  }, []);

  const reloadSummary = useCallback(async () => {
    const { data } = await api.get(`/api/users/${id}/summary/`, { headers: authHeaders() });
    setSummary(data);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        await reloadSummary();
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadSummary]);

  useEffect(() => {
    if (tab === "interviews" && !canSeeInterviews) {
      setTab("overview");
      return;
    }
    if (tab === "interviews_taken" && !canSeeInterviewsTaken) {
      setTab("overview");
    }
  }, [tab, canSeeInterviews, canSeeInterviewsTaken]);

  // Load standard tab data
  useEffect(() => {
    if (!summary) return;

    (async () => {
      let url = "";

      if (tab === "groups") {
        url = `/api/users/${id}/groups/`;
        const { data } = await api.get(url, { headers: authHeaders() });
        setRows(Array.isArray(data) ? data : []);
        setAttendanceRows([]);
        setAttendanceStats(null);
        return;
      }

      if (tab === "attendance") {
        const params = new URLSearchParams();
        if (fromDate) params.set("from", fromDate);
        if (toDate) params.set("to", toDate);
        const qs = params.toString() ? `?${params}` : "";

        if (isAdmin) {
          const [groupedRes, entriesRes, statsRes] = await Promise.all([
            api.get(`/api/users/${id}/attendance/${qs}`, { headers: authHeaders() }),
            api.get(`/api/users/${id}/attendance_entries/${qs}`, { headers: authHeaders() }),
            api.get(`/api/users/${id}/attendance_stats/${qs}`, { headers: authHeaders() }),
          ]);

          setRows(Array.isArray(groupedRes.data) ? groupedRes.data : []);
          setAttendanceRows(Array.isArray(entriesRes.data) ? entriesRes.data : []);
          setAttendanceStats(statsRes.data || null);
        } else {
          const [attendanceRes, statsRes] = await Promise.all([
            api.get(`/api/users/${id}/attendance/${qs}`, { headers: authHeaders() }),
            api.get(`/api/users/${id}/attendance_stats/${qs}`, { headers: authHeaders() }),
          ]);

          setRows(Array.isArray(attendanceRes.data) ? attendanceRes.data : []);
          setAttendanceRows([]);
          setAttendanceStats(statsRes.data || null);
        }
        return;
      }

      if (tab === "interviews") {
        if (!canSeeInterviews) {
          setRows([]);
          setAttendanceStats(null);
          return;
        }
        url = `/api/users/${id}/interviews/`;
        const { data } = await api.get(url, { headers: authHeaders() });
        setRows(Array.isArray(data) ? data : []);
        setAttendanceRows([]);
        setAttendanceStats(null);
        return;
      }

      if (tab === "incidents") {
        url = `/api/users/${id}/incidents/`;
        const { data } = await api.get(url, { headers: authHeaders() });
        setRows(Array.isArray(data) ? data : []);
        setAttendanceRows([]);
        setAttendanceStats(null);
        return;
      }

      setRows([]);
      setAttendanceRows([]);
      setAttendanceStats(null);
    })();
  }, [tab, id, fromDate, toDate, summary, isAdmin, canSeeInterviews, tabRefresh]);

  // Load interviews taken by this person
  useEffect(() => {
    if (tab !== "interviews_taken") return;

    if (!canSeeInterviewsTaken) {
      setTakenRows([]);
      return;
    }

    (async () => {
      try {
        const params = {};
        if (tStart) params.from = tStart;
        if (tEnd) params.to = tEnd;
        if (tQ.trim()) params.q = tQ.trim();
        params.interviewer = id;

        const { data } = await api.get("/api/interviews/", {
          params,
          headers: authHeaders(),
        });

        setTakenRows(Array.isArray(data) ? data : []);
      } catch {
        setTakenRows([]);
      }
    })();
  }, [tab, id, tQ, tStart, tEnd, canSeeInterviewsTaken, tabRefresh]);

  // ---- Admin / helper actions ----

  const bumpTabRefresh = () => setTabRefresh((v) => v + 1);

  const updateField = async (patch) => {
    try {
      await api.patch(`/api/users/${id}/`, patch, { headers: authHeaders() });
      await reloadSummary();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === "object" ? JSON.stringify(e.response.data) : null) ||
        "Tókst ekki að uppfæra notanda.";
      alert(msg);
    }
  };

  const deleteMember = async () => {
    if (!window.confirm("Ertu viss um að eyða meðlim? Þetta er óafturkræft.")) return;
    try {
      await api.delete(`/api/users/${id}/`, { headers: authHeaders() });
      nav("/members");
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að eyða.");
    }
  };

  const changePassword = async () => {
    const pwd = window.prompt("Nýtt lykilorð (minnst 8 stafir):");
    if (!pwd) return;
    try {
      await api.post(`/api/users/${id}/change_password/`, { password: pwd }, { headers: authHeaders() });
      alert("Lykilorði breytt.");
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að breyta lykilorði.");
    }
  };

  const toggleActiveStatus = async () => {
    try {
      await api.post(
        `/api/users/${id}/set_active/`,
        { is_active: !summary?.is_active },
        { headers: authHeaders() }
      );
      await reloadSummary();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að breyta stöðu notanda.");
    }
  };

  const editFullName = () => {
    openEditor({
      title: "Breyta nafni",
      initial: summary?.full_name || "",
      placeholder: "Fullt nafn…",
      validate: (v) => ((v || "").trim() ? "" : "Vantar nafn."),
      onSave: async (v) => {
        await updateField({ full_name: v.trim() });
      },
    });
  };

  const editUsername = () => {
    openEditor({
      title: "Breyta notendanafni",
      initial: summary?.username || "",
      placeholder: "t.d. jona23",
      validate: (v) => {
        const s = (v || "").trim();
        if (!s) return "Vantar notendanafn";
        if (s.length < 3 || s.length > 30) return "Notendanafn þarf að vera 3–30 stafir";
        if (!USERNAME_RE.test(s)) return "Leyfilegt: a–z, 0–9, punktur, undirstrik, bandstrik";
        return "";
      },
      onSave: async (v) => {
        await updateField({ username: v.trim() });
      },
    });
  };

  const toggleAdminRole = async () => {
    try {
      await api.post(
        `/api/users/${id}/set_admin/`,
        { is_admin: !summary?.is_admin },
        { headers: authHeaders() }
      );
      await reloadSummary();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að breyta admin stöðu.");
    }
  };

  const togglePeerSupport = () => updateField({ is_peer_support: !summary?.is_peer_support });
  const toggleBlueBoxExclusion = () => updateField({ exclude_from_bluebox: !summary?.exclude_from_bluebox });

  const toggleVolunteer = async () => {
    const next = !summary.is_volunteer;

    if (next && !summary.description_of_volunteer_position) {
      openEditor({
        title: "Lýsing á sjálfboðastarfi (skylt)",
        initial: "",
        multiline: true,
        placeholder: "Lýstu sjálfboðastarfi viðkomandi…",
        validate: (v) => ((v || "").trim() ? "" : "Lýsing er nauðsynleg."),
        onSave: async (v) => {
          await updateField({
            is_volunteer: true,
            description_of_volunteer_position: v.trim(),
          });
        },
      });
      return;
    }

    await updateField({ is_volunteer: next });
  };

  const editVolunteerDesc = () => {
    openEditor({
      title: "Lýsing á sjálfboðastarfi",
      initial: summary?.description_of_volunteer_position || "",
      multiline: true,
      placeholder: "Lýstu sjálfboðastarfi viðkomandi…",
      onSave: async (v) => {
        await updateField({ description_of_volunteer_position: (v || "").trim() || null });
      },
    });
  };

  const editEmail = () => {
    openEditor({
      title: "Breyta netfangi",
      initial: summary?.email || "",
      placeholder: "nafn@example.com",
      validate: (v) => (EMAIL_RE.test((v || "").trim()) ? "" : "Ógilt netfang (t.d. nafn@example.com)."),
      onSave: async (v) => { await updateField({ email: v.trim() }); },
    });
  };

  const editPhone = () => {
    openEditor({
      title: "Breyta símanúmeri",
      initial: normalizePhoneToDash(summary?.phone_number || ""),
      placeholder: "555-5555",
      validate: (v) => (PHONE7_RE.test(normalizePhoneToDash(v)) ? "" : "Sími þarf að vera 555-5555."),
      onSave: async (v) => { await updateField({ phone_number: normalizePhoneToDash(v) }); },
    });
  };

  const editPlace = () => {
    openEditor({
      title: "Skipta um sveitarfélag",
      initial: summary?.place || "",
      placeholder: "Sveitarfélag…",
      validate: (v) => ((v || "").trim() ? "" : "Vantar sveitarfélag."),
      onSave: async (v) => { await updateField({ place: v.trim() }); },
    });
  };

  const editNOK = () => {
    openEditor({
      title: "Breyta nánasta aðstandanda",
      initial: summary?.next_of_kin || "",
      placeholder: "Nafn nánasta aðstandanda…",
      validate: (v) => ((v || "").trim() ? "" : "Vantar nafn nánasta aðstandanda."),
      onSave: async (v) => { await updateField({ next_of_kin: v.trim() }); },
    });
  };

  const editNOKPhone = () => {
    openEditor({
      title: "Breyta símanúmeri aðstandanda",
      initial: normalizePhoneToDash(summary?.phone_number_next_of_kin || ""),
      placeholder: "555-5555",
      validate: (v) => (PHONE7_RE.test(normalizePhoneToDash(v)) ? "" : "Sími aðstandanda þarf að vera 555-5555."),
      onSave: async (v) => { await updateField({ phone_number_next_of_kin: normalizePhoneToDash(v) }); },
    });
  };

  const toggleRehab = () => updateField({ is_in_rehab: !summary.is_in_rehab });
  const toggleInUrraedi = () => updateField({ is_in_urraedi: !summary.is_in_urraedi });
  const toggleTeamsari = () => updateField({ is_teamsari: !summary.is_teamsari });

  const editCurrentRehab = () => {
    openEditor({
      title: "Skipulag endurhæfingar",
      initial: summary?.current_rehab || "",
      multiline: true,
      placeholder: "Lýsing á skipulagi endurhæfingar…",
      onSave: async (v) => { await updateField({ current_rehab: v || null }); },
    });
  };

  const toggleLeader = () => updateField({ is_group_leader: !summary.is_group_leader });
  const toggleStaff = () => updateField({ is_staff_member: !summary.is_staff_member });

  const editSex = () => {
    openChoice({
      title: "Kyn",
      options: SEX_OPTIONS,
      initial: summary?.sex || "other",
      onSave: async (val) => { await updateField({ sex: val }); },
    });
  };

  const editDescription = () => {
    openEditor({
      title: "Lýsing (stutt athugasemd)",
      initial: summary?.description || "",
      multiline: true,
      placeholder: "Skrifaðu lýsingu…",
      onSave: async (v) => { await updateField({ description: v || "" }); },
    });
  };

  const openAttendanceEditor = (row) => {
    setAttendanceEdit({
      id: row.id,
      kind: row.kind,
      attendance_date: row.date || "",
    });
    setAttendanceEditOpen(true);
  };

  const saveAttendanceEdit = async () => {
    try {
      await api.patch(
        `/api/attendance/entry/${attendanceEdit.kind}/${attendanceEdit.id}/`,
        { attendance_date: attendanceEdit.attendance_date },
        { headers: authHeaders() }
      );
      setAttendanceEditOpen(false);
      await reloadSummary();
      bumpTabRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að uppfæra mætingu.");
    }
  };

  const deleteAttendanceEntry = async (row) => {
    if (!window.confirm("Ertu viss um að eyða þessari mætingu?")) return;
    try {
      await api.delete(`/api/attendance/entry/${row.kind}/${row.id}/`, {
        headers: authHeaders(),
      });
      await reloadSummary();
      bumpTabRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að eyða mætingu.");
    }
  };

  const openInterviewEditor = (row) => {
    setInterviewEdit({
      id: row.id,
      interview_date: row.interview_date || "",
      kind: row.did_not_arrive ? "no_show" : row.in_house ? "in_house" : row.on_phone ? "remote" : "",
      flex: !!row.flex,
      peer_support: !!row.peer_support,
      rehab_planning: !!row.rehab_planning,
      rehab_interview: !!row.rehab_interview,
      urgent_interview: !!row.urgent_interview,
      family_interview: !!row.family_interview,
      visit: !!row.visit,
      handleidsla: !!row.handleidsla,
      handleidsla_nemar: !!row.handleidsla_nemar,
      ororka: !!row.ororka,
    });
    setInterviewEditOpen(true);
  };

  const saveInterviewEdit = async () => {
    if (!interviewEdit.kind) {
      alert("Veldu tegund viðtals.");
      return;
    }

    try {
      await api.patch(
        `/api/interviews/${interviewEdit.id}/`,
        {
          interview_date: interviewEdit.interview_date,
          did_not_attend: interviewEdit.kind === "no_show",
          in_house: interviewEdit.kind === "in_house",
          on_phone: interviewEdit.kind === "remote",
          flex: interviewEdit.flex,
          peer_support: interviewEdit.peer_support,
          rehab_planning: interviewEdit.rehab_planning,
          rehab_interview: interviewEdit.rehab_interview,
          urgent_interview: interviewEdit.urgent_interview,
          family_interview: interviewEdit.family_interview,
          visit: interviewEdit.visit,
          handleidsla: interviewEdit.handleidsla,
          handleidsla_nemar: interviewEdit.handleidsla_nemar,
          ororka: interviewEdit.ororka,
        },
        { headers: authHeaders() }
      );
      setInterviewEditOpen(false);
      bumpTabRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að uppfæra viðtal.");
    }
  };

  const deleteInterviewEntry = async (row) => {
    if (!window.confirm("Ertu viss um að eyða þessu viðtali?")) return;
    try {
      await api.delete(`/api/interviews/${row.id}/`, { headers: authHeaders() });
      bumpTabRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að eyða viðtali.");
    }
  };

  const openIncidentEditor = (row) => {
    setIncidentEdit({
      id: row.id,
      incident_date: row.incident_date || "",
      description: row.description || "",
      time_taken: row.time_taken || "",
      in_house: !!row.in_house,
      on_phone: !!row.on_phone,
      email: !!row.email,
    });
    setIncidentEditOpen(true);
  };

  const saveIncidentEdit = async () => {
    try {
      await api.patch(
        `/api/incidents/${incidentEdit.id}/`,
        {
          incident_date: incidentEdit.incident_date,
          description: incidentEdit.description,
          time_taken: incidentEdit.time_taken,
          in_house: incidentEdit.in_house,
          on_phone: incidentEdit.on_phone,
          email: incidentEdit.email,
        },
        { headers: authHeaders() }
      );
      setIncidentEditOpen(false);
      bumpTabRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að uppfæra atvik.");
    }
  };

  const deleteIncidentEntry = async (row) => {
    if (!window.confirm("Ertu viss um að eyða þessu atviki?")) return;
    try {
      await api.delete(`/api/incidents/${row.id}/`, { headers: authHeaders() });
      bumpTabRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að eyða atviki.");
    }
  };

  // --- Client-side filtering helpers ---

  const dateIn = (iso, start, end) => {
    if (!iso) return false;
    const d = String(iso);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };

  const filteredAttendanceRows = attendanceRows.filter((x) => {
    const okDate = (!fromDate && !toDate) || dateIn(x.date, fromDate, toDate);
    return okDate;
  });

  const filteredIntervieweeRows = rows.filter((x) => {
    const okDate = (!iStart && !iEnd) || dateIn(x.interview_date, iStart, iEnd);
    const okQ = iQ.trim()
      ? String(x.interviewer_name || "").toLowerCase().includes(iQ.trim().toLowerCase())
      : true;
    return okDate && okQ;
  });

  const filteredTakenRows = takenRows.filter((x) => {
    const okDate = (!tStart && !tEnd) || dateIn(x.interview_date, tStart, tEnd);
    const okQ = tQ.trim()
      ? String(x.user_name || "").toLowerCase().includes(tQ.trim().toLowerCase())
      : true;
    return okDate && okQ;
  });

  const attendanceSummary = attendanceStats?.summary || null;
  const attendanceGroupBreakdown = Array.isArray(attendanceStats?.groups) ? attendanceStats.groups : [];

  return (
    <Shell
      title="Hugarafl starfsfólk"
      rightMenu
      menuItems={[
        { title: "Samþykkja nýjan félagsaðila", to: "/members/approve" },
        { title: "Yfirlit yfir félagsaðila", to: "/members" },
        { title: "Yfirlit yfir hópa", to: "/groups" },
        { title: "Skrá viðtöl", to: "/interviews/new" },
        { title: "Skrá atvik", to: "/incidents/new" },
        { title: "Skrá mætingu í hóp", to: "/attendance/group" },
        { title: "Heildaryfirlit yfir mætingu", to: "/attendance/summary" },
      ]}
    >
      {loading || !summary ? (
        <div>Hleð…</div>
      ) : (
        <>
          <h2 style={{ margin: "6px 0 14px" }}>{summary.full_name}</h2>

          {isAdmin && (
            <div className="overview-topbar">
              <button className="btn ghost sm" onClick={() => nav(`/interviews/new?user=${encodeURIComponent(id)}`)}>
                Skrá viðtal
              </button>
              <button className="btn ghost sm" onClick={() => nav(`/incidents/new?user=${encodeURIComponent(id)}`)}>
                Skrá atvik
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <TabBtn active={tab === "overview"} onClick={() => setTab("overview")}>
              Yfirlit á einstakling
            </TabBtn>

            <TabBtn active={tab === "groups"} onClick={() => setTab("groups")}>
              Skráðir hópar
            </TabBtn>

            <TabBtn active={tab === "attendance"} onClick={() => setTab("attendance")}>
              Mæting
            </TabBtn>

            {canSeeInterviews && (
              <TabBtn active={tab === "interviews"} onClick={() => setTab("interviews")}>
                Viðtöl
              </TabBtn>
            )}

            {canSeeInterviewsTaken && (
              <TabBtn active={tab === "interviews_taken"} onClick={() => setTab("interviews_taken")}>
                Viðtöl þessa einstaklings
              </TabBtn>
            )}

            <TabBtn active={tab === "incidents"} onClick={() => setTab("incidents")}>
              Atvik
            </TabBtn>
          </div>

          {/* OVERVIEW */}
          {tab === "overview" && (
            <div className="placeholder kv">
              <DL
                label="Fullt nafn"
                value={summary.full_name || "—"}
                action={isAdmin && (<button className="btn ghost sm" onClick={editFullName}>Breyta nafni</button>)}
              />

              <DL
                label="Notendanafn"
                value={summary.username}
                action={isAdmin && (<button className="btn ghost sm" onClick={editUsername}>Breyta notendanafni</button>)}
              />

              <DL label="Kennitala" value={summary.ssn} />

              <DL
                label="Email"
                value={summary.email}
                action={isAdmin && (<button className="btn ghost sm" onClick={editEmail}>Breyta netfangi</button>)}
              />

              <DL
                label="Símanúmer"
                value={summary.phone_number}
                action={isAdmin && (<button className="btn ghost sm" onClick={editPhone}>Breyta símanúmeri</button>)}
              />

              <DL
                label="Sveitarfélag"
                value={summary.place}
                action={isAdmin && (<button className="btn ghost sm" onClick={editPlace}>Skipta um sveitarfélag</button>)}
              />

              <DL
                label="Nánasti aðstandandi"
                value={summary.next_of_kin}
                action={isAdmin && (<button className="btn ghost sm" onClick={editNOK}>Breyta aðstandanda</button>)}
              />

              <DL
                label="Símanúmer aðstandanda"
                value={summary.phone_number_next_of_kin}
                action={isAdmin && (<button className="btn ghost sm" onClick={editNOKPhone}>Breyta símanúmeri aðstandanda</button>)}
              />

              <DL label="Mætti síðast" value={fmt(summary.attended_last)} />

              <DL
                label="Er í endurhæfingu"
                value={summary.is_in_rehab ? "Já" : "Nei"}
                action={canManageRehabAndUraedi && (<button className="btn ghost sm" onClick={toggleRehab}>Breyta endurhæfingu</button>)}
              />

              <DL
                label="Er teamsari"
                value={summary.is_teamsari ? "Já" : "Nei"}
                action={canManageRehabAndUraedi && (
                  <button className="btn ghost sm" onClick={toggleTeamsari}>
                    {summary.is_teamsari ? "Afskrá Teamsara" : "Breyta í Teamsara"}
                  </button>
                )}
              />

              <DL
                label="Í úrræði"
                value={summary.is_in_urraedi ? "Já" : "Nei"}
                action={canManageRehabAndUraedi && (
                  <button className="btn ghost sm" onClick={toggleInUrraedi}>
                    {summary.is_in_urraedi ? "Taka úr úrræði" : "Setja í úrræði"}
                  </button>
                )}
              />

              <DL label="Byrjaði í Hugarafli" value={fmt(summary.started_at_hugarafl)} />

              <DL
                label="Er hópstjóri"
                value={summary.is_group_leader ? "Já" : "Nei"}
                action={isAdmin && (
                  <button className="btn ghost sm" onClick={toggleLeader}>
                    {summary.is_group_leader ? "Afskrá hópstjóra" : "Breyta í hópstjóra"}
                  </button>
                )}
              />

              <DL
                label="Er starfsfólk"
                value={summary.is_staff_member ? "Já" : "Nei"}
                action={isAdmin && (
                  <button className="btn ghost sm" onClick={toggleStaff}>
                    {summary.is_staff_member ? "Afskrá starfsfólk" : "Breyta í starfsfólk"}
                  </button>
                )}
              />

              <DL
                label="Virkur notandi"
                value={summary.is_active ? "Já" : "Nei"}
                action={isAdmin && (
                  <button className="btn ghost sm" onClick={toggleActiveStatus}>
                    {summary.is_active ? "Gera óvirkan" : "Virkja notanda"}
                  </button>
                )}
              />

              <DL
                label="Sýna í Bláa kassanum"
                value={summary.exclude_from_bluebox ? "Nei" : "Já"}
                action={isAdmin && (
                  <button className="btn ghost sm" onClick={toggleBlueBoxExclusion}>
                    {summary.exclude_from_bluebox
                      ? "Sýna aftur í Bláa kassanum"
                      : "Fela úr Bláa kassanum"}
                  </button>
                )}
              />

              <DL
                label="Er jafningjastuðningsaðili"
                value={summary.is_peer_support ? "Já" : "Nei"}
                action={isAdmin && (
                  <button className="btn ghost sm" onClick={togglePeerSupport}>
                    {summary.is_peer_support ? "Afskrá jafningjastuðning" : "Breyta í jafningjastuðning"}
                  </button>
                )}
              />

              {isSuperuser && (
                <DL
                  label="Er admin"
                  value={summary.is_admin ? "Já" : "Nei"}
                  action={
                    <button className="btn ghost sm" onClick={toggleAdminRole}>
                      {summary.is_admin ? "Fjarlægja admin" : "Breyta í admin"}
                    </button>
                  }
                />
              )}

              <DL
                label="Er sjálfboðaliði"
                value={summary.is_volunteer ? "Já" : "Nei"}
                action={
                  isAdmin && (
                    <button className="btn ghost sm" onClick={toggleVolunteer}>
                      {summary.is_volunteer ? "Afskrá sjálfboðaliða" : "Breyta í sjálfboðaliða"}
                    </button>
                  )
                }
              />

              <DL
                label="Lýsing á sjálfboðastarfi"
                value={summary.description_of_volunteer_position || "—"}
                action={
                  isAdmin && (
                    <button className="btn ghost sm" onClick={editVolunteerDesc}>
                      Breyta lýsingu sjálfboðastarfs
                    </button>
                  )
                }
              />

              <DL
                label="Skipulag endurhæfingar"
                value={summary.current_rehab || "—"}
                action={canManageRehabAndUraedi && (<button className="btn ghost sm" onClick={editCurrentRehab}>Breyta skipu. endur</button>)}
              />

              <DL
                label="Kyn"
                value={SEX_LABELS_IS[summary.sex] || "—"}
                action={isAdmin && (<button className="btn ghost sm" onClick={editSex}>Breyta kyni</button>)}
              />

              <DL
                label="Lýsing"
                value={summary.description || "—"}
                action={isAdmin && (<button className="btn ghost sm" onClick={editDescription}>Breyta lýsingu</button>)}
              />

              {canChangePassword && (
                <div className="overview-footer">
                  <button className="btn" onClick={changePassword}>Breyta lykilorði</button>
                </div>
              )}

              {isAdmin && (
                <div className="overview-danger">
                  <button className="btn danger" onClick={deleteMember}>Eyða meðlim</button>
                </div>
              )}
            </div>
          )}

          {/* GROUPS */}
          {tab === "groups" && (
            <Table
              headers={["Nafn á hóp", "Mæting í hóp", "Mætti síðast", "Nauðsynleg mæting"]}
              rows={rows
                .filter((r) => !/^Mæting (eingöngu í hús|í hús)$/i.test(r.name))
                .map((r) => [r.name, r.count, fmt(r.last), r.mandatory ? "Já" : "Nei"])}
            />
          )}

          {/* ATTENDANCE */}
          {tab === "attendance" && (
            <>
              <div className="members-toolbar" style={{ marginBottom: 10 }}>
                <div />
                <div className="filter-block">
                  <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>Sía</button>
                  {filtersOpen && (
                    <div className="filter-popover">
                      <div className="filter-title">Sía eftir dagsetningum:</div>
                      <div className="filter-row">
                        <label>Byrjunardagsetning:</label>
                        <MiniDatePickerDMY value={fromDate} onChange={setFromDate} />
                      </div>
                      <div className="filter-row">
                        <label>Endadagsetning:</label>
                        <MiniDatePickerDMY value={toDate} onChange={setToDate} />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                        <button className="btn ghost sm" onClick={() => { setFromDate(""); setToDate(""); }}>
                          Hreinsa
                        </button>
                        <button className="btn sm" onClick={() => setFiltersOpen(false)}>Loka síu</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {isAdmin ? (
                <>
                  {/* Yfirlit mætingar */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Yfirlit mætingar</div>
                    <Table
                      headers={["Nafn", "Mæting", "Mætti síðast"]}
                      rows={rows.map((r) => [r.name, r.count, fmt(r.last)])}
                    />
                  </div>

                  {/* Mæting í prósentum */}
                  <div
                    style={{
                      marginBottom: 18,
                      background: "#fff",
                      border: "1px solid #d7dbe3",
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
                      Mæting í prósentum
                    </div>
                    <div style={{ opacity: 0.75, marginBottom: 14 }}>
                      Hér er aðeins miðað við mætingu í hópa. Mæting í hús er ekki talin með. 100% jafngildir 3 hópum á viku.
                    </div>

                    {attendanceSummary ? (
                      <>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                            gap: 10,
                            marginBottom: 14,
                          }}
                        >
                          <AttendanceStatCard
                            label="Tímabil"
                            value={`${fmt(attendanceSummary.from)} – ${fmt(attendanceSummary.to)}`}
                          />
                          <AttendanceStatCard
                            label="Hópamætingar"
                            value={attendanceSummary.group_attended_count ?? 0}
                          />
                          <AttendanceStatCard
                            label="Viðmið (100%)"
                            value={attendanceSummary.target_attendance ?? 0}
                          />
                          <AttendanceStatCard
                            label="Mætingarprósenta"
                            value={fmtPct(attendanceSummary.overall_percent)}
                            accent={
                              Number(attendanceSummary.overall_percent || 0) >= Number(attendanceSummary.minimum_percent || 80)
                                ? "#166534"
                                : "#b91c1c"
                            }
                          />
                          <AttendanceStatCard
                            label="Lágmark"
                            value={`${attendanceSummary.minimum_percent ?? 80}%`}
                          />
                          <AttendanceStatCard
                            label="Staða"
                            value={attendanceSummary.meets_minimum ? "Nær lágmarki" : "Undir lágmarki"}
                            accent={attendanceSummary.meets_minimum ? "#166534" : "#b91c1c"}
                          />
                        </div>

                        <div style={{ fontWeight: 600, margin: "8px 0" }}>
                          Mætingarprósenta eftir hópum
                        </div>

                        <div className="table-wrap">
                          <table className="table zebra">
                            <thead>
                              <tr>
                                <th>Hópur</th>
                                <th>Mætt</th>
                                <th>Haldinn</th>
                                <th>Prósenta</th>
                                <th>Mætti síðast</th>
                              </tr>
                            </thead>
                            <tbody>
                              {attendanceGroupBreakdown.length === 0 ? (
                                <tr>
                                  <td colSpan={5} style={{ padding: 16, textAlign: "center" }}>
                                    Engin hópamæting á völdu tímabili
                                  </td>
                                </tr>
                              ) : (
                                attendanceGroupBreakdown.map((g) => (
                                <tr key={g.group_id || g.name}>
                                  <td>{g.name || "—"}</td>
                                  <td>{g.attended_count ?? 0}</td>
                                  <td>{g.held_count ?? 0}</td>
                                  <td>{fmtPct(g.percent)}</td>
                                  <td>{fmt(g.last)}</td>
                                </tr>
                              ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div style={{ opacity: 0.7 }}>Engin prósentugögn tiltæk.</div>
                    )}
                  </div>

                  {/* Stakar mætingarskráningar */}
                  <div style={{ fontWeight: 600, margin: "8px 0" }}>Stakar mætingarskráningar</div>
                  <div className="table-wrap">
                    <table className="table zebra">
                      <thead>
                        <tr>
                          <th>Tegund</th>
                          <th>Hópur</th>
                          <th>Dagsetning</th>
                          <th>Skráð af</th>
                          <th>Aðgerðir</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttendanceRows.length === 0 ? (
                          <tr>
                            <td colSpan={5} style={{ padding: 16, textAlign: "center" }}>
                              Engin gögn
                            </td>
                          </tr>
                        ) : (
                          filteredAttendanceRows.map((r) => (
                            <tr key={`${r.kind}-${r.id}`}>
                              <td>{r.kind === "house" ? "Hús" : "Hópur"}</td>
                              <td>{r.group_name || "—"}</td>
                              <td>{fmt(r.date)}</td>
                              <td>{r.marked_by || "—"}</td>
                              <td style={{ display: "flex", gap: 8 }}>
                                <button className="btn ghost sm" onClick={() => openAttendanceEditor(r)}>
                                  Breyta
                                </button>
                                <button className="btn danger sm" onClick={() => deleteAttendanceEntry(r)}>
                                  Eyða
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ textAlign: "center", marginTop: 10, opacity: 0.8 }}>
                    Heildarfjöldi mætingarskráninga í lista: {filteredAttendanceRows.length}
                  </div>
                </>
              ) : (
                <>
                  <Table
                    headers={["Nafn", "Mæting", "Mætti síðast"]}
                    rows={rows.map((r) => [r.name, r.count, fmt(r.last)])}
                  />

                  <div
                    style={{
                      marginTop: 18,
                      background: "#fff",
                      border: "1px solid #d7dbe3",
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
                      Mæting í prósentum
                    </div>
                    <div style={{ opacity: 0.75, marginBottom: 14 }}>
                      Hér er aðeins miðað við mætingu í hópa. Mæting í hús er ekki talin með. 100% jafngildir 3 hópum á viku.
                    </div>

                    {attendanceSummary ? (
                      <>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                            gap: 10,
                            marginBottom: 14,
                          }}
                        >
                          <AttendanceStatCard
                            label="Tímabil"
                            value={`${fmt(attendanceSummary.from)} – ${fmt(attendanceSummary.to)}`}
                          />
                          <AttendanceStatCard
                            label="Hópamætingar"
                            value={attendanceSummary.group_attended_count ?? 0}
                          />
                          <AttendanceStatCard
                            label="Viðmið (100%)"
                            value={attendanceSummary.target_attendance ?? 0}
                          />
                          <AttendanceStatCard
                            label="Mætingarprósenta"
                            value={fmtPct(attendanceSummary.overall_percent)}
                            accent={
                              Number(attendanceSummary.overall_percent || 0) >= Number(attendanceSummary.minimum_percent || 80)
                                ? "#166534"
                                : "#b91c1c"
                            }
                          />
                          <AttendanceStatCard
                            label="Lágmark"
                            value={`${attendanceSummary.minimum_percent ?? 80}%`}
                          />
                          <AttendanceStatCard
                            label="Staða"
                            value={attendanceSummary.meets_minimum ? "Nær lágmarki" : "Undir lágmarki"}
                            accent={attendanceSummary.meets_minimum ? "#166534" : "#b91c1c"}
                          />
                        </div>

                        <div style={{ fontWeight: 600, margin: "8px 0" }}>
                          Mætingarprósenta eftir hópum
                        </div>

                        <div className="table-wrap">
                          <table className="table zebra">
                            <thead>
                              <tr>
                                <th>Hópur</th>
                                <th>Mætt</th>
                                <th>Haldinn</th>
                                <th>Prósenta</th>
                                <th>Mætti síðast</th>
                              </tr>
                            </thead>
                            <tbody>
                              {attendanceGroupBreakdown.length === 0 ? (
                                <tr>
                                  <td colSpan={5} style={{ padding: 16, textAlign: "center" }}>
                                    Engin hópamæting á völdu tímabili
                                  </td>
                                </tr>
                              ) : (
                                attendanceGroupBreakdown.map((g) => (
                                  <tr key={g.group_id || g.name}>
                                    <td>{g.name || "—"}</td>
                                    <td>{g.attended_count ?? 0}</td>
                                    <td>{g.held_count ?? 0}</td>
                                    <td>{fmtPct(g.percent)}</td>
                                    <td>{fmt(g.last)}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div style={{ opacity: 0.7 }}>Engin prósentugögn tiltæk.</div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* INTERVIEWS */}
          {tab === "interviews" && canSeeInterviews && (
            <>
              <div className="members-toolbar" style={{ marginBottom: 10 }}>
                <div className="search-block">
                  <label className="sr" htmlFor="isearch">Leita</label>
                  <input
                    id="isearch"
                    value={iQ}
                    onChange={(e) => setIQ(e.target.value)}
                    placeholder="Leita að nafni viðtalanda"
                  />
                </div>
                <div className="filter-block">
                  <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>Sía</button>
                  {filtersOpen && (
                    <div className="filter-popover">
                      <div className="filter-title">Sía eftir dagsetningum:</div>
                      <div className="filter-row">
                        <label>Byrjunardagsetning:</label>
                        <MiniDatePickerDMY value={iStart} onChange={setIStart} />
                      </div>
                      <div className="filter-row">
                        <label>Endadagsetning:</label>
                        <MiniDatePickerDMY value={iEnd} onChange={setIEnd} />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                        <button className="btn ghost sm" onClick={() => { setIQ(""); setIStart(""); setIEnd(""); }}>
                          Hreinsa
                        </button>
                        <button className="btn sm" onClick={() => setFiltersOpen(false)}>Loka síu</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="table-wrap">
                <table className="table zebra">
                  <thead>
                    <tr>
                      <th>Viðtalandi</th>
                      <th>Mætti ekki</th>
                      <th>Kom í hús</th>
                      <th>Símaviðtal / Fjarfundur</th>
                      <th>Flex</th>
                      <th>Jafningjastuðningur</th>
                      <th>Endurhæfingaráætlun</th>
                      <th>Endurhæfingarviðtal</th>
                      <th>Bráðaviðtal</th>
                      <th>Fjölskylduviðtal</th>
                      <th>Vitjun</th>
                      <th>Handleiðsla</th>
                      <th>Handleiðsla / nemar</th>
                      <th>Örorka</th>
                      <th>Dagsetning</th>
                      <th>Aðgerðir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIntervieweeRows.length === 0 ? (
                      <tr>
                        <td colSpan={16} style={{ padding: 16, textAlign: "center" }}>
                          Engin gögn
                        </td>
                      </tr>
                    ) : (
                      filteredIntervieweeRows.map((x) => (
                        <tr key={x.id}>
                          <td>{x.interviewer_name || "—"}</td>
                          <td>{x.did_not_arrive ? "Já" : "Nei"}</td>
                          <td>{x.in_house ? "Já" : "Nei"}</td>
                          <td>{x.on_phone ? "Já" : "Nei"}</td>
                          <td>{x.flex ? "Já" : "Nei"}</td>
                          <td>{x.peer_support ? "Já" : "Nei"}</td>
                          <td>{x.rehab_planning ? "Já" : "Nei"}</td>
                          <td>{x.rehab_interview ? "Já" : "Nei"}</td>
                          <td>{x.urgent_interview ? "Já" : "Nei"}</td>
                          <td>{x.family_interview ? "Já" : "Nei"}</td>
                          <td>{x.visit ? "Já" : "Nei"}</td>
                          <td>{x.handleidsla ? "Já" : "Nei"}</td>
                          <td>{x.handleidsla_nemar ? "Já" : "Nei"}</td>
                          <td>{x.ororka ? "Já" : "Nei"}</td>
                          <td>{fmt(x.interview_date)}</td>
                          <td style={{ display: "flex", gap: 8 }}>
                            <button className="btn ghost sm" onClick={() => openInterviewEditor(x)}>
                              Breyta
                            </button>
                            <button className="btn danger sm" onClick={() => deleteInterviewEntry(x)}>
                              Eyða
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ textAlign: "center", marginTop: 10, opacity: 0.8 }}>
                Heildarfjöldi viðtala í lista: {filteredIntervieweeRows.length}
              </div>
            </>
          )}

          {/* INTERVIEWS TAKEN */}
          {tab === "interviews_taken" && canSeeInterviewsTaken && (
            <>
              <div className="members-toolbar" style={{ marginBottom: 10 }}>
                <div className="search-block">
                  <label className="sr" htmlFor="tsearch">Leita</label>
                  <input
                    id="tsearch"
                    value={tQ}
                    onChange={(e) => setTQ(e.target.value)}
                    placeholder="Leita að nafni viðmælanda"
                  />
                </div>
                <div className="filter-block">
                  <button className="btn sm" onClick={() => setTFiltersOpen((v) => !v)}>Sía</button>
                  {tFiltersOpen && (
                    <div className="filter-popover">
                      <div className="filter-title">Sía eftir dagsetningum:</div>
                      <div className="filter-row">
                        <label>Byrjunardagsetning:</label>
                        <MiniDatePickerDMY value={tStart} onChange={setTStart} />
                      </div>
                      <div className="filter-row">
                        <label>Endadagsetning:</label>
                        <MiniDatePickerDMY value={tEnd} onChange={setTEnd} />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                        <button className="btn ghost sm" onClick={() => { setTQ(""); setTStart(""); setTEnd(""); }}>
                          Hreinsa
                        </button>
                        <button className="btn sm" onClick={() => setTFiltersOpen(false)}>Loka síu</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="table-wrap">
                <table className="table zebra">
                  <thead>
                    <tr>
                      <th>Viðmælandi</th>
                      <th>Mætti ekki</th>
                      <th>Kom í hús</th>
                      <th>Símaviðtal / Fjarfundur</th>
                      <th>Flex</th>
                      <th>Jafningjastuðningur</th>
                      <th>Endurhæfingaráætlun</th>
                      <th>Endurhæfingarviðtal</th>
                      <th>Bráðaviðtal</th>
                      <th>Fjölskylduviðtal</th>
                      <th>Vitjun</th>
                      <th>Handleiðsla</th>
                      <th>Handleiðsla / nemar</th>
                      <th>Örorka</th>
                      <th>Dagsetning</th>
                      {isAdmin && <th>Aðgerðir</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTakenRows.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 16 : 15} style={{ padding: 16, textAlign: "center" }}>
                          Engin gögn
                        </td>
                      </tr>
                    ) : (
                      filteredTakenRows.map((x) => (
                        <tr key={x.id}>
                          <td>{x.user_name || "—"}</td>
                          <td>{x.did_not_arrive ? "Já" : "Nei"}</td>
                          <td>{x.in_house ? "Já" : "Nei"}</td>
                          <td>{x.on_phone ? "Já" : "Nei"}</td>
                          <td>{x.flex ? "Já" : "Nei"}</td>
                          <td>{x.peer_support ? "Já" : "Nei"}</td>
                          <td>{x.rehab_planning ? "Já" : "Nei"}</td>
                          <td>{x.rehab_interview ? "Já" : "Nei"}</td>
                          <td>{x.urgent_interview ? "Já" : "Nei"}</td>
                          <td>{x.family_interview ? "Já" : "Nei"}</td>
                          <td>{x.visit ? "Já" : "Nei"}</td>
                          <td>{x.handleidsla ? "Já" : "Nei"}</td>
                          <td>{x.handleidsla_nemar ? "Já" : "Nei"}</td>
                          <td>{x.ororka ? "Já" : "Nei"}</td>
                          <td>{fmt(x.interview_date)}</td>
                          {isAdmin && (
                            <td style={{ display: "flex", gap: 8 }}>
                              <button className="btn ghost sm" onClick={() => openInterviewEditor(x)}>
                                Breyta
                              </button>
                              <button className="btn danger sm" onClick={() => deleteInterviewEntry(x)}>
                                Eyða
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ textAlign: "center", marginTop: 10, opacity: 0.8 }}>
                Heildarfjöldi viðtala í lista: {filteredTakenRows.length}
              </div>
            </>
          )}

          {/* INCIDENTS */}
          {tab === "incidents" && (
            <div className="table-wrap">
              <table className="table zebra">
                <thead>
                  <tr>
                    <th>Lýsing</th>
                    <th>Tími</th>
                    <th>Í hús</th>
                    <th>Sími</th>
                    <th>Email</th>
                    <th>Dagsetning</th>
                    {isAdmin && <th>Aðgerðir</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 7 : 6} style={{ padding: 16, textAlign: "center" }}>
                        Engin gögn
                      </td>
                    </tr>
                  ) : (
                    rows.map((x) => (
                      <tr key={x.id}>
                        <td>{x.description || "—"}</td>
                        <td>{x.time_taken || "—"}</td>
                        <td>{x.in_house ? "Já" : "Nei"}</td>
                        <td>{x.on_phone ? "Já" : "Nei"}</td>
                        <td>{x.email ? "Já" : "Nei"}</td>
                        <td>{fmt(x.incident_date)}</td>
                        {isAdmin && (
                          <td style={{ display: "flex", gap: 8 }}>
                            <button className="btn ghost sm" onClick={() => openIncidentEditor(x)}>
                              Breyta
                            </button>
                            <button className="btn danger sm" onClick={() => deleteIncidentEntry(x)}>
                              Eyða
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Admin edit modals */}
          <AttendanceEditModal
            open={attendanceEditOpen}
            value={attendanceEdit}
            setValue={setAttendanceEdit}
            onCancel={() => setAttendanceEditOpen(false)}
            onSave={saveAttendanceEdit}
          />

          <InterviewEditModal
            open={interviewEditOpen}
            value={interviewEdit}
            setValue={setInterviewEdit}
            onCancel={() => setInterviewEditOpen(false)}
            onSave={saveInterviewEdit}
          />

          <IncidentEditModal
            open={incidentEditOpen}
            value={incidentEdit}
            setValue={setIncidentEdit}
            onCancel={() => setIncidentEditOpen(false)}
            onSave={saveIncidentEdit}
          />

          {/* Existing modals */}
          <EditorModal
            open={editorOpen}
            title={editorTitle}
            multiline={editorMultiline}
            value={editorValue}
            setValue={(v) => {
              if (/símanúmer/i.test(editorTitle)) setEditorValue(normalizePhoneToDash(v));
              else setEditorValue(v);
            }}
            placeholder={editorPlaceholder}
            error={editorError}
            onCancel={() => setEditorOpen(false)}
            onSave={runEditorSave}
          />

          <ChoiceModal
            open={choiceOpen}
            title={choiceTitle}
            value={choiceValue}
            setValue={setChoiceValue}
            options={choiceOptions}
            onCancel={() => setChoiceOpen(false)}
            onSave={runChoiceSave}
          />
        </>
      )}
    </Shell>
  );
}

function DL({ label, value, action }) {
  return (
    <div className="kv-row">
      <div className="kv-label">{label}:</div>
      <div className="kv-value">{value}</div>
      <div className="kv-actions">{action || null}</div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="table-wrap">
      <table className="table zebra">
        <thead>
          <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: 16, textAlign: "center" }}>
                Engin gögn
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => <td key={j}>{c}</td>)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}